import { VStack, HStack, Card, Text, Heading, Badge, Skeleton } from 'wss3-forge'
import {
  History20Regular,
  Desktop20Regular,
  Phone20Regular,
  Tablet20Regular,
  Checkmark12Regular,
  Dismiss12Regular
} from '@fluentui/react-icons'
import type { LoginHistory } from '../../types/database'

interface LoginHistoryCardProps {
  history: LoginHistory[]
  loading: boolean
}

const deviceIcons = {
  desktop: <Desktop20Regular />,
  mobile: <Phone20Regular />,
  tablet: <Tablet20Regular />
}

type TimePeriod = 'today' | 'yesterday' | 'this_week' | 'last_week' | 'this_month' | 'older'

function getTimePeriod(date: string): TimePeriod {
  const d = new Date(date)
  const now = new Date()

  // Reset to start of day for comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const entryDate = new Date(d.getFullYear(), d.getMonth(), d.getDate())

  // Get start of this week (Monday)
  const startOfWeek = new Date(today)
  startOfWeek.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1))

  // Get start of last week
  const startOfLastWeek = new Date(startOfWeek)
  startOfLastWeek.setDate(startOfLastWeek.getDate() - 7)

  // Get start of this month
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)

  if (entryDate.getTime() >= today.getTime()) return 'today'
  if (entryDate.getTime() >= yesterday.getTime()) return 'yesterday'
  if (entryDate.getTime() >= startOfWeek.getTime()) return 'this_week'
  if (entryDate.getTime() >= startOfLastWeek.getTime()) return 'last_week'
  if (entryDate.getTime() >= startOfMonth.getTime()) return 'this_month'
  return 'older'
}

function getPeriodLabel(period: TimePeriod): string {
  const labels: Record<TimePeriod, string> = {
    today: 'Today',
    yesterday: 'Yesterday',
    this_week: 'This week',
    last_week: 'Last week',
    this_month: 'This month',
    older: 'Older'
  }
  return labels[period]
}

function formatTime(date: string): string {
  const d = new Date(date)
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

function formatDate(date: string): string {
  const d = new Date(date)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const days = Math.floor(hours / 24)

  if (hours < 1) return 'Just now'
  if (hours < 24) return formatTime(date)
  if (days < 7) return `${days}d ago`
  return d.toLocaleDateString('fr-FR')
}

function getAuthMethodLabel(method: string): string {
  const labels: Record<string, string> = {
    email: 'Email',
    oauth_google: 'Google',
    oauth_azure: 'Microsoft',
    oauth_github: 'GitHub',
    oauth_discord: 'Discord',
    magic_link: 'Magic Link',
    passkey: 'Passkey'
  }
  return labels[method] || method
}

function groupByPeriod(history: LoginHistory[]): Map<TimePeriod, LoginHistory[]> {
  const groups = new Map<TimePeriod, LoginHistory[]>()
  const order: TimePeriod[] = ['today', 'yesterday', 'this_week', 'last_week', 'this_month', 'older']

  // Initialize empty arrays for each period
  order.forEach(period => groups.set(period, []))

  history.forEach(entry => {
    const period = getTimePeriod(entry.created_at)
    groups.get(period)!.push(entry)
  })

  return groups
}

export function LoginHistoryCard({ history, loading }: LoginHistoryCardProps) {
  if (loading) {
    return (
      <Card padding="md">
        <VStack gap="md">
          <HStack gap="sm" style={{ alignItems: 'center' }}>
            <History20Regular style={{ color: 'var(--brand-primary)' }} />
            <Heading level={5}>Login History</Heading>
          </HStack>
          <VStack gap="sm">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} width="100%" height={40} />
            ))}
          </VStack>
        </VStack>
      </Card>
    )
  }

  return (
    <Card padding="md">
      <VStack gap="md">
        <HStack gap="sm" style={{ alignItems: 'center' }}>
          <History20Regular style={{ color: 'var(--brand-primary)' }} />
          <Heading level={5}>Login History</Heading>
          {history.length > 0 && (
            <Badge variant="default">{history.length}</Badge>
          )}
        </HStack>

        {history.length === 0 ? (
          <Text color="muted" size="sm">No login history available</Text>
        ) : (
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <VStack gap="md">
              {(() => {
                const grouped = groupByPeriod(history)
                const periods: TimePeriod[] = ['today', 'yesterday', 'this_week', 'last_week', 'this_month', 'older']

                return periods.map(period => {
                  const entries = grouped.get(period) || []
                  if (entries.length === 0) return null

                  return (
                    <VStack key={period} gap="xs">
                      <Text size="xs" weight="medium" color="muted" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {getPeriodLabel(period)}
                      </Text>
                      {entries.map((entry) => (
                        <div
                          key={entry.id}
                          style={{
                            padding: '0.75rem',
                            backgroundColor: 'var(--bg-tertiary)',
                            borderRadius: '8px'
                          }}
                        >
                          <HStack gap="md" style={{ alignItems: 'center' }}>
                            <Text color="muted">
                              {deviceIcons[entry.device_type as keyof typeof deviceIcons] || <Desktop20Regular />}
                            </Text>
                            <VStack gap="xs" style={{ flex: 1 }}>
                              <HStack gap="sm" style={{ alignItems: 'center' }}>
                                <Text size="sm" weight="medium">
                                  {entry.browser || 'Unknown'} on {entry.os || 'Unknown'}
                                </Text>
                                {entry.success ? (
                                  <Checkmark12Regular style={{ color: 'var(--color-success)' }} />
                                ) : (
                                  <Dismiss12Regular style={{ color: 'var(--color-error)' }} />
                                )}
                              </HStack>
                              <Text size="xs" color="muted">
                                {getAuthMethodLabel(entry.auth_method)}
                              </Text>
                            </VStack>
                            <Text size="xs" color="muted">
                              {formatDate(entry.created_at)}
                            </Text>
                          </HStack>
                        </div>
                      ))}
                    </VStack>
                  )
                })
              })()}
            </VStack>
          </div>
        )}
      </VStack>
    </Card>
  )
}
